import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { updateProductSchema } from '@lombok/shared';
import type { Product, UpdateProductInput } from '@lombok/shared';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { BackLink, DetailRow } from '@/components/DetailRow';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { WIDE_COLUMN } from '@/components/layout';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { QueryState } from '@/components/QueryState';
import { CheckboxField, FormActions, FormError, TextareaField, TextField } from '@/components/form';
import { useDeleteProduct, useProduct, useUpdateProduct } from '@/data/hooks';
import { notifyMutationError } from '@/lib/mutationToasts';
import { BrandName, BrandSelect, CategoryName, CategorySelect } from './BrandCategoryFields';
import { ProductSkusCard } from './ProductSkusCard';

const BASE = '/app/admin/productos';

/**
 * Producto detail (admin) with inline edit. Read mode shows the catalog record;
 * "Editar" swaps to an RHF form validated by the shared `updateProductSchema`.
 */
export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const query = useProduct(id);
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const update = useUpdateProduct(id ?? '');
  const remove = useDeleteProduct();
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState<'archive' | 'delete' | null>(null);
  const product = query.data;

  return (
    <div className={`flex flex-col gap-6 ${WIDE_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={BASE}>Productos</BackLink>
        <PageHeader
          title={query.data?.name ?? 'Producto'}
          description="Detalle del producto en el catálogo."
          actions={
            product && !editing && isAdmin ? (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  Editar
                </Button>
                {product.archived ? (
                  <Button size="sm" variant="outline" onClick={() => void update.mutateAsync({ archived: false }).catch((e: unknown) => notifyMutationError(e, undefined))}>
                    Restaurar
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setConfirm('archive')}>
                    Archivar
                  </Button>
                )}
                <Button size="sm" variant="danger" onClick={() => setConfirm('delete')}>
                  Eliminar
                </Button>
              </div>
            ) : undefined
          }
        />
      </div>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        loadingLabel="Cargando producto…"
      >
        {query.data ? (
          editing ? (
            <ProductEditForm product={query.data} onDone={() => setEditing(false)} />
          ) : (
            <div className="flex flex-col gap-6">
              <ProductSummary product={query.data} />
              <ProductSkusCard productId={query.data.productId} />
            </div>
          )
        ) : null}
      </QueryState>

      <ConfirmDialog
        open={confirm === 'archive'}
        title="Archivar producto"
        confirmLabel="Archivar"
        onConfirm={() => update.mutateAsync({ archived: true })}
        onClose={() => setConfirm(null)}
      >
        El producto deja de mostrarse en el catálogo y en las listas, pero se conserva con todo su historial. Podrás
        restaurarlo cuando quieras.
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === 'delete'}
        title="Eliminar producto"
        confirmLabel="Eliminar definitivamente"
        danger
        onConfirm={async () => {
          await remove.mutateAsync(id ?? '');
          navigate(BASE);
        }}
        onClose={() => setConfirm(null)}
      >
        Se elimina para siempre junto con sus presentaciones (SKUs). Solo es posible si el producto no tiene inventario,
        pedidos ni cotizaciones abiertas; si los tiene, archívalo en su lugar.
      </ConfirmDialog>
    </div>
  );
}

function ProductSummary({ product: p }: { product: Product }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Información del producto</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <dl className="flex flex-col">
          <DetailRow label="Nombre">{p.name}</DetailRow>
          <DetailRow label="Descripción">{p.description?.trim() || '—'}</DetailRow>
          <DetailRow label="Estado">
            <Badge tone={p.archived ? 'neutral' : p.active ? 'success' : 'warning'}>
              {p.archived ? 'Archivado' : p.active ? 'Activo' : 'Inactivo'}
            </Badge>
          </DetailRow>
          <DetailRow label="Marca">
            <BrandName id={p.brandId} />
          </DetailRow>
          <DetailRow label="Categoría">
            <CategoryName id={p.categoryId} />
          </DetailRow>
        </dl>
      </CardContent>
    </Card>
  );
}

function ProductEditForm({ product: p, onDone }: { product: Product; onDone: () => void }) {
  const mutation = useUpdateProduct(p.productId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<UpdateProductInput>({
    resolver: zodResolver(updateProductSchema),
    defaultValues: {
      name: p.name,
      description: p.description ?? '',
      brandId: p.brandId,
      categoryId: p.categoryId,
      active: p.active,
    },
  });

  useEffect(() => {
    reset({
      name: p.name,
      description: p.description ?? '',
      brandId: p.brandId,
      categoryId: p.categoryId,
      active: p.active,
    });
  }, [p, reset]);

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, { onSuccess: () => onDone() });
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Editar producto</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          <FormError error={mutation.error} />

          <TextField label="Nombre" required error={errors.name?.message} {...register('name')} />
          <TextareaField
            label="Descripción"
            error={errors.description?.message}
            {...register('description')}
          />
          <div className="grid gap-5 sm:grid-cols-2">
            <BrandSelect
              required
              current={p.brandId}
              error={errors.brandId?.message}
              {...register('brandId')}
            />
            <CategorySelect
              required
              current={p.categoryId}
              error={errors.categoryId?.message}
              {...register('categoryId')}
            />
          </div>
          <CheckboxField label="Activo" {...register('active')} />

          <FormActions>
            <Button type="button" variant="outline" onClick={onDone}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !isDirty}>
              {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </FormActions>
        </form>
      </CardContent>
    </Card>
  );
}
